/**
 * analytics — Google Analytics 4, deliberately scoped to ONE page.
 *
 * PURPOSE (the whole of it): count how many people land on /register and do
 * not go on to sign up. The signup side of that subtraction comes from the
 * database, which is the system of record for anyone who actually has an
 * account. GA is here only for the half the database structurally cannot see:
 * visitors who never became a row.
 *
 * SCOPE — /register ONLY. gtag.js is loaded lazily by trackRegisterPageView()
 * and is therefore never requested on any other route. This is not a
 * configuration preference, it is the design: ThinkOtter is a children's
 * product, and the student learning surfaces must never carry a third-party
 * analytics tag. Do NOT move this into index.html, and do NOT call it from a
 * router-level listener — either change silently puts Google on every page a
 * child uses. If GA is ever wanted beyond the signup door, the privacy policy
 * has to change first (the same rule the marketing site's assets/js/ga.js
 * states in its own header).
 *
 * PROPERTY: the SAME measurement ID as the marketing site. www.thinkotter.sg
 * and app.thinkotter.sg are subdomains of one registrable domain, so GA4's
 * default cookie_domain ('auto') writes _ga on thinkotter.sg and both surfaces
 * share it. That is why there is no cross-domain linker config here and no
 * change needed in the website repo: subdomains are not cross-domain, and the
 * marketing cta_click event and this page_view land in one funnel by default.
 * Never set cookie_domain explicitly — doing so breaks that join.
 *
 * QUERY STRINGS ARE STRIPPED (non-negotiable): /register is reached as
 * /register?ref=CODE (TS-015 deep link). GA4's automatic page_view would send
 * the full URL, handing that referral code to Google. So send_page_view is
 * disabled in the config call and the page_view is emitted manually with a
 * page_location built from origin + pathname only. Nothing the visitor typed,
 * and no code, ever reaches the wire.
 *
 * GUARDRAILS mirror the marketing site's ga.js: Google Signals and ad
 * personalisation off in code as well as in the property admin.
 *
 * FAIL-SAFE (TO-LL-036): every export wraps its body in a try/catch that never
 * re-throws, following the established gateTelemetry.ts / errorReporter.ts
 * pattern. An analytics failure must never break the signup page — this is an
 * observability sink with no data-integrity operation to protect, which is the
 * advisory-exception carve-out TO-LL-036 itself describes.
 *
 * NO-OP WITHOUT AN ID: VITE_GA_MEASUREMENT_ID is deliberately left unset in
 * dev and in the test environment, so dev traffic never pollutes the property
 * and the whole module is inert across the existing test suite.
 */

/** Set once gtag.js has been injected, so a remount never loads it twice. */
let initialised = false;

/** The marketing site's property. Same ID on purpose — see PROPERTY above. */
function getMeasurementId(): string | undefined {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  return id && id.trim() !== '' ? id.trim() : undefined;
}

/**
 * The current URL with any query string and fragment removed, e.g.
 * 'https://app.thinkotter.sg/register' even when the visitor arrived on
 * '/register?ref=ABC123'. Never returns the raw href.
 */
function sanitisedPageLocation(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Load gtag.js (once) and record a page view for the registration page.
 *
 * Call ONLY from RegisterPage. Idempotent, so React 19 <StrictMode>'s
 * double-invoked effects in dev send one page_view, not two. Never throws.
 */
export function trackRegisterPageView(): void {
  try {
    const measurementId = getMeasurementId();
    if (!measurementId || typeof window === 'undefined' || typeof document === 'undefined') {
      // Unset in dev/test, or no DOM (jsdom lacks nothing here, but the suite
      // has no measurement ID, so this is the silent no-op path).
      return;
    }

    const w = window as typeof window & {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };

    if (!initialised) {
      w.dataLayer = w.dataLayer || [];
      // gtag pushes `arguments` itself — it must NOT be an arrow function and
      // must not spread, or the dataLayer entries lose their expected shape.
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer!.push(arguments);
      }
      w.gtag = w.gtag || (gtag as (...args: unknown[]) => void);

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      document.head.appendChild(script);

      w.gtag('js', new Date());
      w.gtag('config', measurementId, {
        // Suppress the automatic page_view: it would send the full URL
        // including ?ref=CODE. The manual event below sends a stripped one.
        send_page_view: false,
        // Same guardrails as the marketing site's ga.js.
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });

      initialised = true;
    }

    w.gtag?.('event', 'page_view', {
      page_location: sanitisedPageLocation(),
      page_title: 'Register',
    });
  } catch {
    // Never let an analytics failure surface on the signup page.
  }
}

/** Test-only reset of the load-once latch. Not used by application code. */
export function __resetAnalyticsForTest(): void {
  initialised = false;
}
